import { Analytics } from '@/Telemetry';
import { useAppStore } from '@/inertia/AppStore';
import React from 'react';
import { TelemetrySyncSession } from './syncSession';

// This class is initialized when the page loads
// It listens for the locationchange event and updates the datalayer with the new page data
class InfoBox {
  public constructor() {
    window.addEventListener('locationchange', (ev) => {
      if (ev.detail.type !== 'pushstate') return;

      if (useAppStore.getState()?.isDashboard === true) return;

      this.runInfoBox();
    });
  }

  private runInfoBox() {
    // update the datalayer with the new page data
    // allow 300ms delay for any updates to the page to complete
    setTimeout(() => {
      // update document title
      const { playerNode, pageType, znglist, znglyInfo, hub, isAppPreview } = useAppStore.getState();
      if (isAppPreview) return;

      const titleElement = document.querySelector('title');
      // @todo: sometimes going to the hub page too quickly will cause the hub data to be null
      //        we need to set the title when the data is loaded instead of waiting for the locationchange event
      //        Flow: go to home page -> select a hub quickly before the hub data is loaded -> the title will not be updated
      if (
        !!titleElement &&
        titleElement !== undefined &&
        !!znglyInfo?.browserTabTitle &&
        (!!playerNode?.title || !!znglist?.title || !!hub?.title)
      ) {
        if (pageType === 'front_page' && !!znglyInfo?.browserTabTitle) {
          titleElement.setHTMLUnsafe(znglyInfo.browserTabTitle);
        } else if (pageType === 'znglist' && !!znglist?.title)
          titleElement.setHTMLUnsafe(
            `${znglyInfo.browserTabTitle} | ${znglist?.title?.slice?.(0, 1)?.toUpperCase?.() + znglist?.title.slice(1)}`
          );
        else if (pageType === 'hub' && !!hub?.title) {
          titleElement.setHTMLUnsafe(
            `${znglyInfo.browserTabTitle} | ${hub?.title.slice(0, 1).toUpperCase() + hub?.title.slice(1)}`
          );
        } else if (pageType !== null && !!playerNode?.title) {
          titleElement.setHTMLUnsafe(
            `${znglyInfo.browserTabTitle} | ${playerNode?.title.slice(0, 1).toUpperCase() + playerNode?.title.slice(1)}`
          );
        } else {
          titleElement.setHTMLUnsafe(znglyInfo.browserTabTitle);
        }
      }

      // push new pageview to marketo
      if (!!window.Munchkin) {
        const { location } = window;

        window.Munchkin.munchkinFunction('visitWebPage', {
          url: location.pathname,
          params: location.search,
        });

        TelemetrySyncSession.getInstance().syncMarketo();
      }

      // push new pageview to hubspot
      if (!!window?._hsq && Array.isArray(window._hsq)) {
        const { location } = window;

        let path = location.pathname;
        if (['znglist', 'hub'].includes(pageType ?? '') && !!location.search && !!location.search?.length) {
          path += location.search;
        }

        window._hsq.push(['setPath', path]);
        window._hsq.push(['trackPageView']);
        TelemetrySyncSession.getInstance().syncHubspot();
      }

      // push new pageview to gtag
      InfoBox.pushViewContent();
    }, 300);
  }

  // determine whether we can push to the datalayer
  // compare the current location to the last item pushed to the datalayer
  public static checkIsNewContentOnLocationUpdate() {
    let dl = !!window?.dataLayer ? [...window.dataLayer] : [];

    // if there is no datalayer, we can push
    if (!dl || !dl.length) return true;

    // filter out any items that are not pageviews
    dl = dl.filter((item) => item?.length && item?.length > 2 && item?.[1] === 'zngly_content_view');
    if (!dl || !dl.length) return true;

    // get the last item pushed to the datalayer
    const lastItem = dl.pop();
    const dlEvent = lastItem?.[2];

    const gtagData = Analytics.data();

    if (
      gtagData?.title === dlEvent?.title &&
      gtagData?.url === dlEvent?.url &&
      gtagData?.pageType === dlEvent?.pageType
    ) {
      return false;
    }

    return true;
  }

  public static pushViewContent() {
    if (!InfoBox.checkIsNewContentOnLocationUpdate()) return;

    const data = Analytics.data();

    // if there is infobox data to push, push it
    if (Object.keys(data).length === 0) return;

    // if there is no title return
    if (!data?.title) return;

    Analytics.push('zngly_content_view', {
      eventaction: 'view_content',
      eventlabel: 'zngly_content_view',
    });
  }
}

/**
 * This function is used to push the view content event to the datalayer
 * It should only be used when fetching data from graphql on inital page load.
 * This is a fix until we can refactor the way we handle the datalayer
 */
export const __analyticsContentViewFix__ = () => {
  React.useEffect(() => {
    setTimeout(() => {
      InfoBox.pushViewContent();
    }, 250);
  }, []);
};

export default InfoBox;
